/**
 * Copyright 2026 AutoZone, Inc.
 * Content is confidential to and proprietary information of AutoZone, Inc., its
 * subsidiaries and affiliates.
 */

/* eslint-disable @typescript-eslint/no-unsafe-return, @typescript-eslint/no-explicit-any */

// eslint-disable-next-line no-restricted-imports
import type { AnchorBlockContent } from '@/features/contentstack/lib/common/enhancedPdpContentTypes';
import type { ContentStackContent } from './content-types';

/**
 * Converts Enhanced PDP AnchorBlockContent (GraphQL format with Connection pattern)
 * to legacy ContentStackContent format (flat object with arrays)
 *
 * @param anchorBlockContents - Array of AnchorBlockContent union types from Enhanced PDP
 * @returns Partial ContentStackContent object with extracted content arrays
 *
 * @example
 * const anchorBlocks: AnchorBlockContent[] = [
 *   { __typename: 'AnchorBlockContentBanner', banner: { bannerConnection: { totalCount: 1, edges: [...] } } }
 * ];
 * const contentStackFormat = convertAnchorBlockContentToContentStackContent(anchorBlocks);
 * // Result: { banner: { banner: [...] } }
 *
 * @note Uses 'any' type assertions for node conversion due to structural differences
 * between Enhanced PDP GraphQL types and legacy ContentStack types.
 * Only fields that exist in ContentStack types will be accessible at runtime.
 * Extra fields (e.g., 'system' metadata) are ignored during conversion.
 */
export function convertAnchorBlockContentToContentStackContent(
  anchorBlockContents: AnchorBlockContent[],
): Partial<ContentStackContent> {
  const result: Partial<ContentStackContent> = {};

  for (const content of anchorBlockContents) {
    switch (content.__typename) {
      case 'AnchorBlockContentBanner':
        result.banner = {
          banner: content.banner.bannerConnection.edges.map((e) => e.node as any),
        };
        break;

      case 'AnchorBlockContentBannerSplit':
        result.banner_split = {
          banner_split: content.banner_split.banner_splitConnection.edges.map((e) => e.node as any),
        };
        break;

      case 'AnchorBlockContentProductChart':
        result.product_chart = {
          product_chart: content.product_chart.product_chartConnection.edges.map(
            (e) => e.node as any,
          ),
        };
        break;

      case 'AnchorBlockContentFaq':
        result.faq = {
          faq: content.faq.faqConnection.edges.map((e) => e.node as any),
        };
        break;

      case 'AnchorBlockContentImageContent':
        result.image_content = {
          image_content: content.image_content.image_contentConnection.edges.map(
            (e) => e.node as any,
          ),
        };
        break;

      case 'AnchorBlockContentParttypeGrid':
        result.parttype_grid = {
          parttype_grid: content.parttype_grid.parttype_gridConnection.edges.map(
            (e) => e.node as any,
          ),
        };
        break;

      case 'AnchorBlockContentCategoryGrid':
        result.category_grid = {
          category_grid: content.category_grid.category_gridConnection.edges.map(
            (e) => e.node as any,
          ),
        };
        break;

      case 'AnchorBlockContentLogoDescription':
        result.logo_description = {
          logo_description: content.logo_description.logo_descriptionConnection.edges.map(
            (e) => e.node as any,
          ),
        };
        break;

      case 'AnchorBlockContentImageContentBackground':
        result.image_content_background = {
          image_content_background:
            content.image_content_background.image_content_backgroundConnection.edges.map(
              (e) => e.node as any,
            ),
        };
        break;

      case 'AnchorBlockContentImageCategoryGrid':
        result.image_category_grid = {
          image_category_grid: content.image_category_grid.image_category_gridConnection.edges.map(
            (e) => e.node as any,
          ),
        };
        break;

      case 'AnchorBlockContentMenuList':
        result.menu_list = {
          menu_list: content.menu_list.menu_listConnection.edges.map((e) => e.node as any),
        };
        break;

      case 'AnchorBlockContentBannerCta':
        result.banner_cta = {
          banner_cta: content.banner_cta.banner_ctaConnection.edges.map((e) => e.node as any),
        };
        break;

      case 'AnchorBlockContentProductGrid':
        result.product_grid = {
          product_grid: content.product_grid.product_gridConnection.edges.map((e) => e.node as any),
        };
        break;

      case 'AnchorBlockContentRichTextEditor':
        result.rich_text_editor = {
          rich_text_editor: content.rich_text_editor.rich_text_editorConnection.edges.map(
            (e) => e.node as any,
          ),
        };
        break;

      case 'AnchorBlockContentBlogtypeGrid':
        result.blogtype_grid = {
          blogtype_grid: content.blogtype_grid.blogtype_gridConnection.edges.map(
            (e) => e.node as any,
          ),
        };
        break;

      case 'AnchorBlockContentFeaturedContent':
        result.featured_content = {
          featured_content: content.featured_content.featured_contentConnection.edges.map(
            (e) => e.node as any,
          ),
        };
        break;

      case 'AnchorBlockContentVideoContent':
        result.video_content = {
          video_content: content.video_content.video_contentConnection.edges.map(
            (e) => e.node as any,
          ),
        };
        break;

      case 'AnchorBlockContentButtonGrid':
        result.button_grid = {
          button_grid: content.button_grid.button_gridConnection.edges.map((e) => e.node as any),
        };
        break;

      case 'AnchorBlockContentImageVideoSplit':
        result.image_video_split = {
          image_video_split: content.image_video_split.image_video_splitConnection.edges.map(
            (e) => e.node as any,
          ),
        };
        break;

      case 'AnchorBlockContentIconGrid':
        result.icon_grid = {
          icon_grid: content.icon_grid.icon_gridConnection.edges.map((e) => e.node as any),
        };
        break;

      case 'AnchorBlockContentSuperDuperHero':
        result.super_duper_hero = {
          super_duper_hero: content.super_duper_hero.super_duper_heroConnection.edges.map(
            (e) => e.node as any,
          ),
        };
        break;

      case 'AnchorBlockContentCertona':
        result.certona = {
          certona: content.certona.certonaConnection.edges.map((e) => e.node as any),
        };
        break;

      case 'AnchorBlockContentExperienceInjector':
        result.experience_injector = {
          experience_injector: content.experience_injector.experience_injectorConnection.edges.map(
            (e) => e.node as any,
          ),
        };
        break;

      case 'AnchorBlockContentTargetDiv':
        result.target_div = {
          target_div: content.target_div.target_divConnection.edges.map((e) => e.node as any),
        };
        break;

      case 'AnchorBlockContentIframe':
        result.iframe = {
          iframe: content.iframe.iframeConnection.edges.map((e) => e.node as any),
        };
        break;

      case 'AnchorBlockContentCertonaTabbed':
        result.certona_tabbed = {
          certona_tabbed: content.certona_tabbed.certona_tabbedConnection.edges.map(
            (e) => e.node as any,
          ),
        };
        break;

      case 'AnchorBlockContentDealsSplit':
        result.deals_split = {
          deals_split: content.deals_split.deals_splitConnection.edges.map((e) => e.node as any),
        };
        break;

      case 'AnchorBlockContentPencilBanner':
        result.pencil_banner = {
          pencil_banner: content.pencil_banner.pencil_bannerConnection.edges.map(
            (e) => e.node as any,
          ),
        };
        break;

      case 'AnchorBlockContentLiveTextHero':
        result.live_text_hero = {
          live_text_hero: content.live_text_hero.live_text_heroConnection.edges.map(
            (e) => e.node as any,
          ),
        };
        break;

      default: {
        // Exhaustive check - TypeScript will error if we miss a case
        const _exhaustive: never = content;
        void _exhaustive;
        break;
      }
    }
  }

  return result;
}
